<template lang="pug">
b-card-body.other-office-message
  //- message title
  b-card-title 
    .d-flex.align-items-top
      //- title or delete message
      .d-flex.flex-fill.mr-2
        //- title
        .title(v-if="value.deleteFlag == false && value.isEditing == false") {{ value.title }}
        //- edit title
        .title-input.flex-fill(v-else-if="value.deleteFlag == false && value.isEditing == true")
          b-form-input.required(v-model="updateTitle" placeholder="タイトル" name="title" type="text"
            :class="[ { noinput: !updateTitle } ]"
            v-validate="{ required: true, max: 100 }" data-vv-as="タイトル" :maxlength="100")
          .error-messages.mt-1(v-if="errors.has('title')")
            label.error-message(v-for="error in errors.collect('title')") {{ error }}

        //- delete message and undo delete
        .title.undo-delete(v-if="value.deleteFlag == true")
          span.mr-2 このメッセージは削除されました。
          a.undo-delete-message(v-if="value.senderFacilityId == facility.id" @click="onUndoDeleteMessage(value)") 元に戻す
      //- control buttons
      .d-flex.justify-content-end(v-if="value.deleteFlag == false && value.senderFacilityId == facility.id")
        button.btn.mr-2(v-if="value.isEditing == false" @click="onEditMessage(value)") 編集
        .buttons(v-else)
          button.btn.mr-2(@click="onUpdateMessage(value)") 更新
          button.btn(@click="onUpdateCancel(value)") キャンセル
        button.btn(v-if="value.deleteFlag == false && value.isEditing == false" @click="onDeleteMessage(value)") 削除
  //- datetime
  b-card-sub-title.mb-2
    span.sender-facility.mr-2 {{ getSender(value) }}
    span.send-datetime {{ convertSendDatetime(value) }}
  //- message text and files
  b-card-text(v-if="value.deleteFlag == false")
    //- text
    .message-text(v-if="value.isEditing == false") {{ value.text }}
    //- edit text
    .message-text-input(v-else)
      b-form-textarea.required(v-model="updateText" key-name="本文" name="text" placeholder="本文" :class="[ { noinput: !updateText } ]"
        v-validate="{ required: true, max: 1000 }" data-vv-as="本文" :maxlength="1000")
      .error-messages.mt-1(v-if="errors.has('text')")
        label.error-message(v-for="error in errors.collect('text')") {{ error }}

    //- files
    .file(v-if="value.isEditing == false")
      MessageTempFile.mt-2(v-for="(file, index) in value.fileUrls" v-model="value.fileUrls[index]"
        :id="`${value.datetime}-${index}`" :itemWidth="'60%'" @download="download")
    //- edit files
    .file.mt-2(v-else)
      .d-flex.mb-2
        DraggableFiles.draggable-files(v-model="updatedFiles" @delete="onFileDelete" :item-width="'90%'")
      .d-flex.flex-column.justify-content-end
        MultiFileSelecter.mr-2(:name="`${value.datetime}-update-temp-files`" :mimetypes="_mimeTypes" :limit="_registerFilesLimit"
          :width="'100%'" :height="'32px'" :showPreview="false" @input="inputFiles")
        .error-messages.text-break(v-for="error in errors.collect(`${value.datetime}-update-temp-files`)")
          label.error-message {{ error }}
          br

  //- loading modal
  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Prop, Inject, Vue } from "vue-property-decorator";
import { Getter } from "vuex-class";
import { userStore } from "@/store/store";

import Setting from "../../../../public/setting.json";

import { StandardAccountsGetModel } from "@/commons/services/StandardAccounts";
import { FileUrl } from "@/commons/services/OtherOfficeMessages";
import { FacilityGetModel } from "@/commons/services/Facilities";
import S3UrlService from "@/commons/services/S3Url";

import OtherOfficeMessageFileService from "@/commons/services/OtherOfficeMessageFile";

import { OtherOfficeMessageListTabMessage } from "@/pc/containers/sendOtherOfficeMessages/OtherOfficeMessageListTab.vue";
import { OtherOfficeConnectionTableItem } from "./OtherOfficeConnectionTable.vue";

import MessageTempFile from "@/pc/containers/sendOtherOfficeMessages/MessageTempFile.vue";
import MultiFileSelecter from "@/pc/basics/MultiFileSelecter.vue";
import DraggableFiles from "@/pc/components/DraggableFiles.vue";
import DraggableFile from "@/commons/models/DraggableFile";

import PageLoading from "@/pc/basics/PageLoading.vue";

/**
 * ### メッセージの返信入力コンポーネント
 */
@Component({
  components: {
    MessageTempFile,
    MultiFileSelecter,
    DraggableFiles,
    PageLoading
  },
  $_veeValidate: { validator: "new" }
})
export default class OtherOfficeMessage extends Vue {
  /** S3Urlサービス */
  @Inject("S3UrlService") s3UrlService!: S3UrlService;

  /** ユーザー名。ユーザー情報修正画面で更新されるので、storeのgetterから値を取得する */
  @Getter("getFacility") public facility!: FacilityGetModel;

  /** メッセージ */
  @Prop()
  value!: OtherOfficeMessageListTabMessage;
  /** 他事業所連携 */
  @Prop()
  item!: OtherOfficeConnectionTableItem;
  /** 添付ファイル最大数 */
  @Prop()
  fileUploadLimit!: number;
  /** 添付許可Content-Typeリスト */
  @Prop()
  mimeTypes!: string[];
  /** アカウント情報MAP（key：userId、value：アカウント情報） */
  @Prop()
  accountMap!: Map<string, StandardAccountsGetModel>;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";

  /** 更新後タイトル */
  updateTitle: string = "";
  /** 更新後本文 */
  updateText: string = "";
  /** 更新後ファイル */
  updatedFiles: Array<DraggableFile> = [];
  /** 更新用の更新日時 */
  updated: number | null = null;

  get _mimeTypes(): string[] {
    if (this.mimeTypes) {
      return this.mimeTypes;
    } else {
      return [];
    }
  }

  /** ### 登録可能なファイル数を返す */
  get _registerFilesLimit(): number {
    if (this.updatedFiles && this.fileUploadLimit) {
      const limit: number = this.fileUploadLimit - this.updatedFiles.length;
      return limit;
    } else {
      return 0;
    }
  }

  public resetUpdateTopicFiles(): void {
    this.updatedFiles = [];
    return;
  }

  public resetRegisterReplyFiles(): void {
    return;
  }

  public resetUpdateReplyFiles(): void {
    return;
  }

  /**
   * ### 編集ボタン押下イベント
   *
   * - 編集元のメッセージを入力用プロパティに転記する
   * - 編集状態をtrueに切替えてメッセージを編集可能にする
   */
  public async onEditMessage(message: OtherOfficeMessageListTabMessage): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    // 編集元メッセージの情報を転記。ファイルについてはS3から取得してFileオブジェクトにする
    this.updateTitle = message.title;
    this.updateText = message.text;
    const files: Array<DraggableFile> = [];
    for (let i = 0; i < message.fileUrls.length; i++) {
      const file: File = await this.s3UrlService.getFile(userStore, Setting.backets.imageExpireHour, message.fileUrls[i].url, message.fileUrls[i].name);
      const draggableFile: DraggableFile = file;
      draggableFile.key = i.toString();
      files.push(draggableFile);
    }
    this.updatedFiles = files;
    // 編集中に状態変更
    message.isEditing = !message.isEditing;

    this.isBusy = false;

    return;
  }

  /**
   * ### 編集内容の更新ボタン押下イベント
   *
   * - バリデーションしてエラーがあれば何もしない
   * - エラーがなければ親コンポーネントにメッセージと編集内容を通知する
   */
  public async onUpdateMessage(message: OtherOfficeMessageListTabMessage): Promise<void> {
    const validationResult: boolean = await this.$validator.validateAll();
    if (validationResult) {
      this.$emit("onUpdateMessage", { message: message, input: { title: this.updateTitle, text: this.updateText, files: this.updatedFiles } });
    }
    return;
  }

  /**
   * ### 編集内容のキャンセルボタン押下イベント
   *
   * - 入力内容を初期化
   * - 編集状態をfalseに切替えて編集不可にする
   */
  public onUpdateCancel(message: OtherOfficeMessageListTabMessage): void {
    this.updateTitle = "";
    this.updateText = "";
    this.updatedFiles = [];
    message.isEditing = false;
    return;
  }

  /** ### 削除ボタン押下イベントを親コンポーネントに通知する */
  public onDeleteMessage(message: OtherOfficeMessageListTabMessage): void {
    this.$emit("onDeleteMessage", message);
    return;
  }

  /** ### 元に戻す押下イベントを親コンポーネントに通知する */
  public onUndoDeleteMessage(message: OtherOfficeMessageListTabMessage): void {
    this.$emit("onUndoDeleteMessage", message);
    return;
  }

  /**
   * ### 投稿者を取得する
   */
  public getSender(message: OtherOfficeMessageListTabMessage): string {
    if (!this.item) {
      return "";
    }
    if (message.senderFacilityId == this.item.registerdFacilityId) {
      return this.item.officeName;
    } else {
      const latestUserName: string | undefined = this.accountMap.get(message.userId)?.name;
      const userName: string = message.userName ? message.userName : latestUserName ? latestUserName : "";
      return `${userStore.facility.name}（${userName}）`;
    }
  }

  /**
   * ### 日付数値を日付文字列に変換する
   */
  public convertSendDatetime(message: OtherOfficeMessageListTabMessage): string {
    if (!message) {
      return "";
    }
    return this.$dateUtil.convertNumberToString(message.datetime, "yyyy/mm/dd hh:MM");
  }

  /** ### ダウンロードボタン押下イベントを親コンポーネントに通知する */
  public download(fileUrl: FileUrl): void {
    this.$emit("download", fileUrl);
    return;
  }

  public async inputFiles(files: Array<File>): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    const service: OtherOfficeMessageFileService = new OtherOfficeMessageFileService(this.$validator, `${this.value.datetime}-update-temp-files`);
    const addFiles: Array<File> = await service.createAddFile(files, this._registerFilesLimit, this.updatedFiles.length);
    if (0 < addFiles.length) {
      this.updatedFiles = this.updatedFiles.concat(addFiles);

      for (let i = 0; i < this.updatedFiles.length; i++) {
        this.updatedFiles[i].key = i.toString();
      }
    }

    this.isBusy = false;
    return;
  }

  /**
   * ### ファイル削除イベント
   */
  public onFileDelete(index: number): void {
    this.updatedFiles.splice(index, 1);
    return;
  }
}
</script>

<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";
@import "../../../styles/pc/_mixin.scss";

.other-office-message {
  .undo-delete {
    font-weight: bold;
    font-size: 16px;
  }
  .undo-delete-message,
  .show-replies {
    cursor: pointer;
    font-weight: bold;
  }
  .sender-facility {
    font-weight: bold;
  }
  .sender-facility,
  .send-datetime {
    color: $date-font-color;
    text-align: right;
    font-size: 14px;
  }
  .message-text {
    white-space: pre-wrap;
    word-wrap: break-word;
  }
  ::v-deep {
    .form-control {
      color: #000;
    }
    .noinput {
      background-color: $bg-require-input !important;
      &::placeholder {
        color: rgb(117, 117, 117);
      }
    }
  }
}
</style>
